import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, Download, ExternalLink, ArrowLeft, Clock, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

import stripeService from '../../services/stripeService';
import subscriptionVerificationService from '../../services/subscriptionVerificationService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TransactionDetails from './components/TransactionDetails';
import PlanBenefits from './components/PlanBenefits';
import NextSteps from './components/NextSteps';

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState('Verifying your subscription...');

  // Get payment details from navigation state or load from subscription
  const paymentDetails = location?.state?.paymentDetails;
  const expectedPlanType = location?.state?.planType; // Pass from checkout if available

  useEffect(() => {
    verifyAndLoadSubscription();
  }, []);

  const verifyAndLoadSubscription = async () => {
    setLoading(true);
    setVerifying(true);
    setError(null);

    try {
      // Step 1: Poll for subscription activation
      setVerificationStatus('Confirming payment with Stripe...');
      
      const { verified, subscription: verifiedSub, error: verifyError } = 
        await subscriptionVerificationService?.pollSubscriptionStatus(expectedPlanType, {
          maxAttempts: 20,
          intervalMs: 2000,
          timeoutMs: 40000,
          onAttempt: (attempt) => {
            setVerificationAttempts(attempt);
            if (attempt > 5) {
              setVerificationStatus('Syncing subscription data...');
            }
            if (attempt > 10) {
              setVerificationStatus('Almost there, finalizing activation...');
            }
          }
        });

      if (verifyError) {
        // Verification failed or timed out
        setError(verifyError);
        setVerifying(false);
        setLoading(false);
        return;
      }

      if (!verified) {
        // Verification incomplete
        setError('Subscription verification incomplete. Please refresh the page or check your subscription management.');
        setVerifying(false);
        setLoading(false);
        return;
      }

      // Step 2: Subscription verified successfully
      setVerificationStatus('Subscription activated!');
      setSubscription(verifiedSub);
      setVerifying(false);

    } catch (err) {
      console.error('Failed to verify subscription:', err);
      setError('Failed to verify subscription. Please check your subscription management.');
      setVerifying(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      const receiptData = `
PAYMENT RECEIPT - MYBODYTODAY
${'='?.repeat(50)}

Transaction Date: ${new Date()?.toLocaleString()}
Plan: ${subscription?.plan?.name}
Billing Interval: ${subscription?.billingInterval}
Amount: $${subscription?.billingInterval === 'yearly' ? subscription?.plan?.priceYearly : subscription?.plan?.priceMonthly}
Payment Method: ${paymentDetails?.paymentMethod || 'Card ending in ****'}

Billing Period:
Start: ${new Date(subscription?.currentPeriodStart)?.toLocaleDateString()}
End: ${new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}

Next Renewal: ${new Date(subscription?.currentPeriodEnd)?.toLocaleDateString()}

${'='?.repeat(50)}
MyBodyToday - Symptom Tracking Tool
Questions? Contact support@mybodytoday1.com
      `;

      const blob = new Blob([receiptData], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `receipt-${new Date()?.toISOString()?.split('T')?.[0]}.txt`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  const handleAccessPortal = async () => {
    setLoading(true);

    try {
      // Create Stripe portal session
      const { data, error: portalError } = await stripeService?.createPortalSession();

      if (portalError) {
        throw portalError;
      }

      // Redirect to Stripe customer portal
      if (data?.url) {
        stripeService?.redirectToPortal(data?.url);
      }
    } catch (err) {
      console.error('Portal access failed:', err);
      alert(`Failed to access customer portal: ${err?.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/daily-check-in');
  };

  const handleRetryVerification = () => {
    verifyAndLoadSubscription();
  };

  // Verification in progress
  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
            <Clock className="w-10 h-10 text-blue-600 animate-pulse" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Verifying Your Subscription
          </h2>
          
          <p className="text-gray-600 mb-6">
            {verificationStatus}
          </p>

          <div className="mb-6">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>

          {verificationAttempts > 0 && (
            <p className="text-sm text-gray-500">
              Verification attempt {verificationAttempts} of 20
            </p>
          )}

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What's happening?</strong><br />
              We're confirming your payment with Stripe and activating your premium features. This usually takes 5-10 seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading confirmation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verification In Progress</h2>
          
          <p className="text-gray-600 mb-6">
            {error}
          </p>

          <div className="space-y-3">
            <Button 
              onClick={handleRetryVerification} 
              variant="default"
              fullWidth
            >
              Retry Verification
            </Button>
            
            <Button 
              onClick={() => navigate('/subscription-management')} 
              variant="outline"
              fullWidth
            >
              Go to Subscription Management
            </Button>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg text-left">
            <p className="text-sm text-gray-700">
              <strong>Don't worry!</strong> Your payment was successful. The subscription activation may take a few moments to sync. You can:
            </p>
            <ul className="text-sm text-gray-700 mt-2 space-y-1 list-disc list-inside">
              <li>Wait 30 seconds and click "Retry Verification"</li>
              <li>Check your subscription status in Subscription Management</li>
              <li>Contact support if the issue persists</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <Icon name="XCircle" size={48} className="mx-auto" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Details</h2>
          <p className="text-gray-600 mb-4">Subscription not found</p>
          <Button onClick={() => navigate('/subscription-management')} variant="default">
            Go to Subscription Management
          </Button>
        </div>
      </div>
    );
  }

  const isBasicPlan = subscription?.plan?.planType === 'basic';
  const planAmount = subscription?.billingInterval === 'yearly' 
    ? subscription?.plan?.priceYearly 
    : subscription?.plan?.priceMonthly;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/subscription-management')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Subscription
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Indicator */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 bg-success/10 rounded-full mb-6 animate-bounce">
            <CheckCircle2 className="w-12 h-12 md:w-16 md:h-16 text-success" strokeWidth={2} />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Payment Successful!
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
            Your {subscription?.plan?.name} subscription has been activated successfully.
          </p>
          
          <p className="text-sm text-gray-500">
            Confirmation email sent to {user?.email}
          </p>
        </div>

        {/* Transaction Details */}
        <TransactionDetails 
          subscription={subscription}
          planAmount={planAmount}
          paymentMethod={paymentDetails?.paymentMethod}
        />

        {/* Plan Benefits */}
        <PlanBenefits plan={subscription?.plan} />

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={handleDownloadReceipt}
              iconName="Download"
              iconPosition="left"
              fullWidth
            >
              Download Receipt
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              onClick={handleAccessPortal}
              iconName="ExternalLink"
              iconPosition="left"
              fullWidth
            >
              Customer Portal
            </Button>
            
            <Button
              variant="default"
              size="lg"
              onClick={handleGoToDashboard}
              iconName="ArrowRight"
              iconPosition="right"
              fullWidth
            >
              Go to Dashboard
            </Button>
          </div>
        </div>

        {/* Next Steps */}
        <NextSteps plan={subscription?.plan} />

        {/* Support Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Need Help?</h3>
          <p className="text-gray-600 mb-4">
            Our support team is here to assist you with any questions about your subscription.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:support@mybodytoday1.com"
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              support@mybodytoday1.com
            </a>
            <span className="hidden sm:inline text-gray-400">|</span>
            <a 
              href="tel:1-800-MYBODYTODAY" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              1-800-MYBODYTODAY
            </a>
          </div>
        </div>

        {/* Medical Disclaimer */}
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-gray-600 text-center">
            <strong>Medical Disclaimer:</strong> MyBodyToday is a tracking tool only and does not provide medical advice, diagnosis, or treatment. 
            Always consult with qualified healthcare professionals for medical guidance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmation;